import Fuse, { FuseOptionKey } from "fuse.js";
import { round } from "lodash";
import React from "react";
import { MenuItem, MenuItems } from "../../../../../components/@types";
import { TimeEnum } from "../../../../../lib/enums";
import { Selections } from "../../../@types";
import { SEARCH_FIELDS } from "../../../mappings";
import { QSFileFormValues } from "./@types";
import { FileSizeType } from "./components/FileSizeField";

const {
  FILENAME,
  FILETYPE,
  CLASSIFICATION,
  TAG,
  SIZE,
  ATTACK_TECHNIQUE,
  AV_COUNT,
  INDICATORS,
  THREATNAME,
  RISKSCORE
} = SEARCH_FIELDS;

export const convertFileSizeToBytes = (size: number, type: FileSizeType) => {
  switch (type) {
    case "KB":
      return round(size * 1024);
    case "MB":
      return round(size * 1024 * 1024);
    case "GB":
      return round(size * 1024 * 1024 * 1024);
    default:
      return round(size);
  }
};

export const convertFilenameToQuery = (filename: string, option: string) => {
  const trimmedFilename = filename.trim();
  switch (option) {
    case "CONTAINS":
      return `*${trimmedFilename}*`;
    case "MATCHES_EXACTLY":
      return trimmedFilename;
    case "STARTS_WITH":
      return `${trimmedFilename}*`;
    case "ENDS_WITH":
      return `*${trimmedFilename}`;
    default:
      return `*${trimmedFilename}*`;
  }
};

export const convertMenuItemsToQuery = (
  items: MenuItems | undefined,
  surroundItemInQuotes = true,
  separator = ", ",
  surroundQueryWith = { start: "[", end: "]" }
) =>
  items?.length
    ? `${surroundQueryWith.start}${items
        ?.map(item => (surroundItemInQuotes ? `"${item.value}"` : item.value))
        .join(separator)}${surroundQueryWith.end}`
    : undefined;

export const convertNumberRangeToQuery = (
  from: number | undefined,
  to: number | undefined
) => `[${from || "0"} TO ${to || "*"}]`;

const getSelections = (values: QSFileFormValues): Selections | undefined => ({
  period_type: {
    label: values.submissionTime?.label || TimeEnum.LAST_MONTH,
    value: values.submissionTime?.value || TimeEnum.LAST_MONTH
  }
});

export const convertFormValuesToQuery = (values: QSFileFormValues) => {
  const fileNameQuery =
    values.fileName &&
    convertFilenameToQuery(
      values.fileName,
      values.fileNameOption ?? "CONTAINS"
    );

  const fromSizeBytes = values.fromSizeNumber
    ? convertFileSizeToBytes(
        Number(values.fromSizeNumber),
        values.fromSizeType as FileSizeType
      )
    : undefined;
  const toSizeBytes =
    values.toSizeType &&
    convertFileSizeToBytes(
      Number(values.toSizeNumber),
      values.toSizeType as FileSizeType
    );

  const fileType = convertMenuItemsToQuery(values.fileType);
  const fileTags = convertMenuItemsToQuery(values.fileTags);
  const sizeQuery = convertNumberRangeToQuery(fromSizeBytes, toSizeBytes);
  const attackTechnique = convertMenuItemsToQuery(
    values.attackTechnique,
    false
  );
  const classification = convertMenuItemsToQuery(values.classification, false);
  const staticAnalysisIndicators = convertMenuItemsToQuery(
    values.staticAnalysisIndicators,
    false
  );
  const antivirusDetectionsCount = convertNumberRangeToQuery(
    Number(values.antivirusDetectionsCountFrom) ?? undefined,
    Number(values.antivirusDetectionsCountTo) ?? undefined
  );
  const matchedAVVendors = convertMenuItemsToQuery(
    values.avVendors,
    false,
    " OR ",
    { start: "(", end: ")" }
  );

  const queryList = [
    fileNameQuery ? `${FILENAME}"${fileNameQuery}"` : "",
    (values.fileType?.length ?? 0) > 0 ? `${FILETYPE}${fileType}` : "",
    (values.fileTags?.length ?? 0) > 0 ? `${TAG}${fileTags}` : "",
    fromSizeBytes || toSizeBytes ? `${SIZE}${sizeQuery}` : "",
    (values.attackTechnique?.length ?? 0) > 0
      ? `${ATTACK_TECHNIQUE}${attackTechnique}`
      : "",
    values.antivirusDetectionsCountFrom || values.antivirusDetectionsCountTo
      ? `${AV_COUNT}${antivirusDetectionsCount}`
      : "",
    (values.staticAnalysisIndicators?.length ?? 0) > 0
      ? `${INDICATORS}${staticAnalysisIndicators}`
      : "",
    values.threatName ? `${THREATNAME}"${values.threatName}"` : "",
    values.threatScore ? `${RISKSCORE}[${values.threatScore}]` : "",
    (values.classification?.length ?? 0) > 0
      ? `${CLASSIFICATION}${classification}`
      : "",
    (values.avVendors?.length ?? 0) > 0 ? matchedAVVendors : ""
  ];
  const query = queryList.filter(item => item).join(" AND ");
  const selections = getSelections(values);

  return { query, selections };
};

export const getFuzzySearchResults = (
  query: string,
  suggestions: MenuItems,
  keys: FuseOptionKey<MenuItem>[] = ["label", "value"],
  pageSize?: number
) => {
  if (!query) return pageSize ? suggestions.slice(0, pageSize) : suggestions;

  const fuseOptions = {
    threshold: 0.2,
    keys,
    ignoreLocation: true
  };

  const fuzzySearch = new Fuse(suggestions, fuseOptions);

  const results = fuzzySearch.search(query);
  const slicedResults = pageSize ? results.slice(0, pageSize) : results;

  return slicedResults.map(({ item }) => item);
};

// add backsapce
const ENABLED_CHARACTERS = [".", "Backspace"];
export const preventTypingNonNumericCharacters = (e: React.KeyboardEvent) => {
  if (isNaN(Number(e.key)) && !ENABLED_CHARACTERS.includes(e.key)) {
    e.preventDefault();
  }
};
